import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import InquiryList from "./InquiryList";
import * as XLSX from "xlsx";
import ViewNpaInquiries from "./ViewNpaInquiries";
import { getInquiries } from "../../redux/slice/inquirySlice";
import { npaStatsProperties } from "../../redux/slice/propertiesSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../context/LoaderContext";
import { useToast } from "../../context/TostContext";
import { EditIcon } from "../../assets/icons";
import { usePermission } from "../../hooks/usePermission";
import { TabCard } from "../../components/custom-card/Cards";

const MODULE_SLUG = "npa_inquiries";

const Index = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        <span className="text-muted fw-normal">NPA / </span>NPA Inquiries
      </h4>
    </div>
  );
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canExport = hasPermission(MODULE_SLUG, "export");
  const [searchTerm, setSearchTerm] = useState("");
  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  // Pagination
  const [
    limit,
    setLimit
  ] = useState({ value: 10, label: 10 });
  const [currentPage, setCurrentPage] = useState(1);

  const { data = [], meta: { total = 0 } = {} } = useSelector((state) => state.inquiry.inquiries);
  const { npaStats = {} } = useSelector((state) => state.property);

  // Handler for Opening View Modal
  const handleOpenViewModal = (item) => {
    setSelectedInquiry(item);
    setOpenInquiryModal(true);
  };


  const searchTimeout = useRef(null);

  // Debounced Search Handler
  const handleSearch = useCallback(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      dispatch(getInquiries({ search: searchTerm, page: currentPage }));
    }, 600); // 300ms delay for better UX
  }, [searchTerm, currentPage, dispatch]);

  // Trigger API Call when search or page changes
  useEffect(() => {
    handleSearch();
    return () => clearTimeout(searchTimeout.current);
  }, [currentPage, handleSearch]);

  // Api Call for Search
  // useEffect(() => {
  //   const delayTimeout = setTimeout(() => {
  //     dispatch(getInquiries({ search: searchTerm }));
  //   }, 3000); // 3 second delay

  //   // Cleanup function to clear the timeout if searchTerm changes before the delay
  //   return () => clearTimeout(delayTimeout);
  // }, [searchTerm, dispatch]);

  const getAllInquiries = useCallback(() => {
    setLoading(true);
    dispatch(
      getInquiries({
        page: currentPage,
        limit: limit?.value,
      })
    )
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, limit?.value, setLoading, currentPage]);

  useEffect(() => {
    getAllInquiries();
    dispatch(npaStatsProperties());
  }, [getAllInquiries, dispatch]);

  const handleExportInquiry = () => {
    try {
      setLoading(true);
      dispatch(getInquiries({ search: searchTerm, limit: 100000 })).then((res) => {
        const data = res?.payload?.data
        // console.log("data",data);

        if (!data || data.length === 0) {
          toast.error("No data available to Export");
          setLoading(false);
          return;
        }

        // Map the data for export
        const dataToExport = data?.map((item) => {
          return {
            property_id: item?.property?.property_number_id || "-",
            name: item?.user ? `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim() : "-",
            mobile_number: item?.user?.mobile_number || "-",
            email: item?.user?.email || "-",
            property_type: item?.property?.property_type?.name || "-",
            possession_type: item?.property?.possession_type?.name || "-",
            message: item?.message || "-",
            inquiry_created_at: item?.created_at ? new Date(item.created_at).toLocaleString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }) : "-",
          }
        })

        // Export to Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "NPA Inquiries");
        const fileName = "NPA Inquiries(Admin).xlsx";
        XLSX.writeFile(workbook, fileName);
        setLoading(false);
        dispatch(getInquiries({ search: searchTerm, limit: 10 }))
      })
    } catch (error) {
      console.error("Error exporting data: ", error);
      toast.error(error?.message || "Error exporting data");
      setLoading(false); // Disable loading state on error
    }
  };

  const paginationConfig = useMemo(() => ({
    currentPage,
    totalPages: Math.ceil(total / limit?.value) || 1,
    total: total || 0,
    onPageChange: (page) => setCurrentPage(page),
    pageSize: limit?.value,
    setPageSize: (size) => setLimit({ value: size, label: size }),
    setCurrentPage
  }), [currentPage, total, limit]);

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
      <Container fluid className="px-0">
        <div className="grid gap-4">
          <TabCard
            count={npaStats?.total_inquiries || 0}
            title="Total Inquiry"
            className="g-col-4 g-col-xxl-3"
            icon={<EditIcon size={24} />}
            iconClassName="icon-secondary"
          />
        </div>

        {/* List Section */}
        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          <InquiryList
            data={data}
            searchTerm={searchTerm}
            setCurrentPage={setCurrentPage}
            setSearchTerm={setSearchTerm}
            handleOpenViewModal={handleOpenViewModal}
            handleExportInquiry={handleExportInquiry}
            canExport={canExport}
            pagination={paginationConfig}
          />
        </div>
      </Container>

      <ViewNpaInquiries
        isOpen={openInquiryModal}
        selectedInquiry={selectedInquiry}
        onClose={() => setOpenInquiryModal(false)}
        title="Inquiry Details"
      />
    </>
  );
};

export default Index;
